import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import BrandLogo from '../common/BrandLogo';
import {
  Menu as MenuIcon,
  School as LearnIcon,
  AssessmentOutlined as ReportIcon,
  MenuBookOutlined as ExamIcon,
  TimerOutlined as PracticeIcon,
  Note as NoteIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/auth/useAuth';

// 인프런 스타일 헤더
const AppBar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #1d2340;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 64px;
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden;
  @media (max-width: 768px) {
    padding: 0 16px;
    height: 56px;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`;

const NavContainer = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavButton = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 8px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.72);
  font-weight: 600;
  font-size: 14px;
  letter-spacing: -0.02em;
  border-radius: 6px;
  &:hover {
    color: #fff;
    text-decoration: none;
  }
  &.active {
    color: #1d2340;
    background: #ffe58a;
  }
  .MuiSvgIcon-root {
    font-size: 18px;
  }
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AuthButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  text-decoration: none;
  color: #333;
  font-weight: 600;
  font-size: 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  border: 1px solid #e0e0e0;
  background: #fff;
  &:hover {
    background: #f8f8f8;
    border-color: #d0d0d0;
  }
  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

const AuthButtonLogout = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: -0.02em;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: transparent;
  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

// 모바일
const MobileContainer = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const MobileMenuButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  color: #fff;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
  .MuiSvgIcon-root {
    font-size: 24px;
  }
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  display: ${(p) => (p.$open ? 'block' : 'none')};
`;

const DrawerOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
`;

const DrawerContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 280px;
  height: 100%;
  background: #fff;
  padding: 24px 16px;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
`;

const DrawerItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  text-decoration: none;
  color: #333;
  font-weight: 600;
  font-size: 15px;
  border-radius: 10px;
  margin-bottom: 4px;
  transition: background 0.2s;
  &:hover {
    background: #f5f5f5;
  }
  &.active {
    color: #1d2340;
    background: #ffe58a;
  }
  .MuiSvgIcon-root {
    font-size: 22px;
  }
`;

const DrawerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  width: 100%;
  border: none;
  background: none;
  color: #333;
  font-weight: 600;
  font-size: 15px;
  border-radius: 10px;
  margin-bottom: 4px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.2s;
  &:hover {
    background: #f5f5f5;
  }
  .MuiSvgIcon-root {
    font-size: 22px;
  }
`;

const Header = () => {
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/lecture', label: '학습', icon: <LearnIcon /> },
    { path: '/report', label: '리포트', icon: <ReportIcon /> },
    { path: '/exams', label: '기출', icon: <ExamIcon /> },
    { path: '/practice', label: '실전', icon: <PracticeIcon /> },
    { path: '/note', label: '메모', icon: <NoteIcon /> },
    { path: '/payment', label: '수강권', icon: <PaymentsIcon /> },
  ];

  return (
    <AppBar>
      <Toolbar>
        <LogoLink to="/lecture" aria-label="패스로 홈">
          <BrandLogo height={30} inverted />
        </LogoLink>

        <NavContainer>
          {navItems.map((item) => (
            <NavButton
              key={item.path}
              to={item.path}
              className={location.pathname === item.path || location.pathname.startsWith(`${item.path}/`) ? 'active' : ''}
            >
              {item.icon}
              {item.label}
            </NavButton>
          ))}
        </NavContainer>

        <RightGroup>
          <MobileContainer>
            <MobileMenuButton onClick={() => setMobileMenuOpen(true)}>
              <MenuIcon />
            </MobileMenuButton>
          </MobileContainer>
          {isAuthenticated ? (
            <AuthButtonLogout onClick={logout}>
              <LogoutIcon />
              로그아웃
            </AuthButtonLogout>
          ) : (
            <AuthButton to="/login">
              <LoginIcon />
              로그인
            </AuthButton>
          )}
        </RightGroup>
      </Toolbar>

      <Drawer $open={mobileMenuOpen}>
        <DrawerOverlay onClick={() => setMobileMenuOpen(false)} />
        <DrawerContent>
          {navItems.map((item) => (
            <DrawerItem
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={location.pathname === item.path || location.pathname.startsWith(`${item.path}/`) ? 'active' : ''}
            >
              {item.icon}
              {item.label}
            </DrawerItem>
          ))}
          {isAuthenticated ? (
            <DrawerButton
              onClick={() => {
                logout();
                setMobileMenuOpen(false);
              }}
            >
              <LogoutIcon />
              로그아웃
            </DrawerButton>
          ) : (
            <DrawerItem to="/login" onClick={() => setMobileMenuOpen(false)}>
              <LoginIcon />
              로그인
            </DrawerItem>
          )}
        </DrawerContent>
      </Drawer>
    </AppBar>
  );
};

export default Header;
